import { useState, useEffect, useRef } from 'react';

// Tailwind breakpoints (must match tailwind.config.js)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

// Debounce utility (inline to avoid dependencies)
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Calculate screen size object
function getScreenSize(width, height) {
  let breakpoint = 'base';
  if (width >= BREAKPOINTS['2xl']) breakpoint = '2xl';
  else if (width >= BREAKPOINTS.xl) breakpoint = 'xl';
  else if (width >= BREAKPOINTS.lg) breakpoint = 'lg';
  else if (width >= BREAKPOINTS.md) breakpoint = 'md';
  else if (width >= BREAKPOINTS.sm) breakpoint = 'sm';

  return {
    width,
    height,
    isMobile: width < BREAKPOINTS.sm,        // < 640px (phones)
    isTablet: width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg,  // 640-1023px
    isDesktop: width >= BREAKPOINTS.lg,      // >= 1024px
    breakpoint
  };
}

// SSR-safe initial state (defaults to desktop to match server render)
const getInitialState = () => {
  if (typeof window === 'undefined') {
    // Server-side: assume desktop (prevents hydration mismatch)
    return getScreenSize(1024, 768);
  }
  return getScreenSize(window.innerWidth, window.innerHeight);
};

/**
 * useScreenSize - Production-grade screen size detection hook
 *
 * Features:
 * - Debounced resize (150ms) - prevents 100+ events per resize
 * - SSR-safe initialization - prevents hydration mismatch
 * - Cleanup on unmount - prevents memory leaks
 * - Threshold-based updates - only re-renders on breakpoint change
 *
 * @returns {Object} screenSize - { width, height, isMobile, isTablet, isDesktop, breakpoint }
 */
export function useScreenSize() {
  const [screenSize, setScreenSize] = useState(getInitialState);
  const previousBreakpoint = useRef(screenSize.breakpoint);

  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') return;

    const handleResize = debounce(() => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const newSize = getScreenSize(width, height);

      // OPTIMIZATION: Only update state if breakpoint changed
      // This prevents unnecessary re-renders during continuous resize
      if (newSize.breakpoint !== previousBreakpoint.current) {
        previousBreakpoint.current = newSize.breakpoint;
        setScreenSize(newSize);
      }
    }, 150); // 150ms debounce - industry standard

    // Set correct size on mount (in case SSR guess was wrong)
    const initialSize = getScreenSize(window.innerWidth, window.innerHeight);
    if (initialSize.breakpoint !== screenSize.breakpoint) {
      setScreenSize(initialSize);
      previousBreakpoint.current = initialSize.breakpoint;
    }

    window.addEventListener('resize', handleResize);

    // CRITICAL: Cleanup to prevent memory leaks
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return screenSize;
}

/**
 * useIsMobile - Convenience hook for mobile detection
 * @returns {boolean} true if screen width < 640px
 */
export function useIsMobile() {
  const { isMobile } = useScreenSize();
  return isMobile;
}

/**
 * useIsTablet - Convenience hook for tablet detection
 * @returns {boolean} true if screen width is 640-1023px
 */
export function useIsTablet() {
  const { isTablet } = useScreenSize();
  return isTablet;
}

/**
 * useIsDesktop - Convenience hook for desktop detection
 * @returns {boolean} true if screen width >= 1024px
 */
export function useIsDesktop() {
  const { isDesktop } = useScreenSize();
  return isDesktop;
}

/**
 * useBreakpoint - Get current Tailwind breakpoint name
 * @returns {string} Current breakpoint: 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 */
export function useBreakpoint() {
  const { breakpoint } = useScreenSize();
  return breakpoint;
}

export default useScreenSize;
